import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft, Loader2, Users, FolderKanban, FileText, ClipboardCheck,
  TrendingDown, AlertTriangle, Sparkles, BarChart3, Eye, ShieldCheck, ShieldAlert, Mail,
  Activity, Upload, FilePlus, UserPlus, Building2,
} from "lucide-react";

import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

function MetricTile({ label, value, sublabel, icon: Icon, tone = "slate", testid }) {
  // Soft-fill tones matching the MyAnalytics aesthetic — colour says "what
  // kind of thing this is" without screaming. Anchor stat stays ink for
  // dominance; the rest take tinted backgrounds.
  const tones = {
    slate: "bg-slate-900 text-white",
    emerald: "bg-emerald-50 border border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border border-amber-200 text-amber-900",
    blue: "bg-blue-50 border border-blue-200 text-blue-900",
    rose: "bg-rose-50 border border-rose-200 text-rose-900",
  };
  return (
    <div className={`p-5 rounded-md ${tones[tone] || tones.slate}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider font-medium opacity-70">{label}</p>
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
      </div>
      <p className="font-heading text-3xl font-semibold mt-3">{value}</p>
      {sublabel && <p className="text-xs opacity-70 mt-1">{sublabel}</p>}
    </div>
  );
}

function Sparkline({ points }) {
  if (!points || points.length === 0) return null;
  const max = Math.max(1, ...points.map((p) => p.count));
  return (
    <svg viewBox="0 0 280 60" className="w-full h-16" data-testid="programs-timeline-sparkline">
      {points.map((p, i) => {
        const x = (i / Math.max(1, points.length - 1)) * 280;
        const h = (p.count / max) * 56;
        return <rect key={i} x={x - 1.5} y={60 - h} width="3" height={h} fill="#10B981" />;
      })}
    </svg>
  );
}

function FunnelBars({ funnel }) {
  const stages = [
    { key: "created", label: "Programs created" },
    { key: "with_indicators", label: "Indicators added" },
    { key: "with_data", label: "Data ingested" },
    { key: "with_report", label: "Report generated" },
  ];
  const top = funnel.created || 1;
  return (
    <div className="space-y-3" data-testid="funnel-bars">
      {stages.map((s) => {
        const v = funnel[s.key] || 0;
        const pct = Math.round((v / top) * 100);
        return (
          <div key={s.key}>
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-slate-700">{s.label}</span>
              <span className="font-mono text-slate-500">{v} · {pct}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-sm overflow-hidden mt-1.5">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PathSplitDonut({ split }) {
  const total = split.forms_only + split.uploads_only + split.both || 1;
  const segments = [
    { label: "Forms only", value: split.forms_only, color: "#10B981" },
    { label: "Uploads only", value: split.uploads_only, color: "#0F172A" },
    { label: "Both", value: split.both, color: "#94A3B8" },
  ];
  let cum = 0;
  return (
    <div className="flex items-center gap-6" data-testid="path-split-donut">
      <svg viewBox="0 0 120 120" className="w-28 h-28">
        <circle cx="60" cy="60" r="50" fill="none" stroke="#F1F5F9" strokeWidth="14" />
        {segments.map((seg, i) => {
          const frac = seg.value / total;
          if (frac === 0) return null;
          const r = 50;
          const c = 2 * Math.PI * r;
          const dash = frac * c;
          const offset = -cum * c;
          cum += frac;
          return (
            <circle
              key={i}
              cx="60" cy="60" r={r}
              fill="none" stroke={seg.color} strokeWidth="14"
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={offset}
              transform="rotate(-90 60 60)"
            />
          );
        })}
      </svg>
      <ul className="text-xs space-y-1.5">
        {segments.map((s, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
            <span className="text-slate-700">{s.label}</span>
            <span className="font-mono text-slate-500 ml-1">{s.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ErrorTable({ rows, columns, testid }) {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-slate-400 italic">No data yet.</p>;
  }
  return (
    <table className="w-full text-sm" data-testid={testid}>
      <thead>
        <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
          {columns.map((c) => <th key={c.key} className="text-left py-2 font-medium">{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-slate-50 last:border-b-0">
            {columns.map((c) => (
              <td key={c.key} className={`py-2 ${c.key === columns[columns.length - 1].key ? "text-right font-mono text-slate-700" : "text-slate-700"}`}>
                {r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HealthBadge({ status }) {
  const map = {
    ok: { bg: "bg-emerald-50", text: "text-emerald-700", label: "OK" },
    missing: { bg: "bg-rose-50", text: "text-rose-700", label: "MISSING" },
    optional_missing: { bg: "bg-slate-100", text: "text-slate-500", label: "OPTIONAL" },
    sandbox_misconfig: { bg: "bg-rose-50", text: "text-rose-700", label: "SANDBOX" },
  };
  const m = map[status] || map.missing;
  return <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${m.bg} ${m.text}`}>{m.label}</span>;
}

function HealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [probeBusy, setProbeBusy] = useState(false);
  const [probeResult, setProbeResult] = useState(null);
  const [probeRecipient, setProbeRecipient] = useState("");
  useEffect(() => {
    api.get("/health")
      .then(({ data: d }) => setData(d))
      .catch((err) => setError(err.response?.data?.detail || "Couldn't load /api/health"))
      .finally(() => setLoading(false));
  }, []);

  const runProbe = async () => {
    setProbeBusy(true);
    setProbeResult(null);
    try {
      const body = probeRecipient.trim() ? { to_email: probeRecipient.trim() } : {};
      const { data: r } = await api.post("/health/test-email", body);
      setProbeResult(r);
    } catch (err) {
      setProbeResult({ sent: false, error: err.response?.data?.detail || err.message, error_kind: "request_failed" });
    } finally {
      setProbeBusy(false);
    }
  };

  const KIND_HINTS = {
    suppression: "Recipient is on Resend's suppression list (bounced, complained, or unsubscribed previously). Remove them at resend.com/suppressions and they'll receive emails again.",
    domain_mismatch: "Your From-address domain doesn't match a verified domain. Re-verify arrayengine.net at resend.com/domains.",
    key_scope: "Your RESEND_API_KEY is scoped to a different domain or has read-only permission. Generate a new key at resend.com/api-keys with 'Full access' to arrayengine.net.",
    account_restricted: "Resend has rate-limited or restricted your account. Check resend.com → Account → check for billing/verification holds.",
    sandbox_recipient_restriction: "Free-plan accounts can only send to verified recipients. Either upgrade the plan or verify each recipient at resend.com.",
    forbidden: "Resend returned 403 with no specific reason. Check the dashboard's logs page for the exact response.",
    auth: "API key is invalid or missing required permissions. Rotate the key at resend.com/api-keys.",
    validation: "Resend rejected the request as invalid. Check the message below for the failing field.",
    rate_limited: "Resend daily/hourly rate limit hit (free plan = 100/day). Either upgrade or throttle outbound sends.",
    timeout: "Resend API didn't respond in 15s — usually transient. Retrying often clears it.",
    connection: "TCP/TLS connection to Resend failed. Check egress / firewall on the k8s pod.",
    not_configured: "RESEND_API_KEY env var isn't set on this environment.",
  };
  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-5 mb-8" data-testid="health-panel-loading">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white border border-rose-200 rounded-md p-5 mb-8" data-testid="health-panel-error">
        <p className="text-sm text-rose-700">{error}</p>
      </div>
    );
  }
  const overall = data.status;
  const overallTone = overall === "ok" ? "emerald" : overall === "degraded" ? "amber" : "rose";
  const toneClass = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
  }[overallTone];
  return (
    <section className="mb-10" data-testid="health-panel">
      {data.env.some((row) => row.status === "sandbox_misconfig") && (
        <div
          className="mb-4 p-4 rounded-md border border-rose-300 bg-rose-50 flex items-start gap-3"
          data-testid="health-sandbox-banner"
        >
          <AlertTriangle className="w-5 h-5 text-rose-700 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-rose-800">SENDER_EMAIL is pointing at Resend's sandbox</p>
            <p className="text-sm text-rose-700 mt-1 leading-snug">
              Update the <code className="font-mono bg-white px-1 py-0.5 rounded text-xs">SENDER_EMAIL</code> Custom Key
              on this environment to <code className="font-mono bg-white px-1 py-0.5 rounded text-xs">noreply@arrayengine.net</code>,
              then redeploy. Until you do, every verification email, password reset, and weekly digest will be sent from
              an unverified sandbox address and rejected by Resend.
            </p>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
          {overall === "ok" ? <ShieldCheck className="w-5 h-5 text-emerald-600" /> : <ShieldAlert className="w-5 h-5 text-amber-600" />}
          System health
        </h2>
        <span className={`text-xs font-mono uppercase px-2 py-1 rounded-sm border ${toneClass}`} data-testid="health-overall-status">{overall}</span>
      </div>
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <p className="label-overline">Resend (email)</p>
          <p className="text-sm text-slate-700 mt-2 font-mono">{data.resend.sender_email || "—"}</p>
          <div className="mt-3 text-xs space-y-1.5">
            <div className="flex justify-between"><span className="text-slate-500">Domain</span><span className="font-mono text-slate-700">{data.resend.domain || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Status</span>
              <span className={`font-mono ${data.resend.verified ? "text-emerald-700" : "text-rose-700"}`} data-testid="health-resend-status">
                {data.resend.status}
              </span>
            </div>
            {data.resend.verified && data.resend.sender_domain_matches_verified === false && (
              <p className="text-rose-700 leading-snug mt-2" data-testid="health-resend-domain-mismatch">
                ⚠ SENDER_EMAIL domain doesn't match the verified Resend domain. This is the #1 cause of 403s.
              </p>
            )}
            {data.resend.error && <p className="text-rose-600 mt-2 leading-snug">{data.resend.error}</p>}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <p className="label-overline">MongoDB</p>
          <p className="text-sm text-slate-700 mt-2 font-mono">{data.mongo.db_name}</p>
          <div className="mt-3 text-xs space-y-1.5">
            <div className="flex justify-between"><span className="text-slate-500">Status</span>
              <span className={`font-mono ${data.mongo.status === "ok" ? "text-emerald-700" : "text-rose-700"}`} data-testid="health-mongo-status">
                {data.mongo.status}
              </span>
            </div>
            <div className="flex justify-between"><span className="text-slate-500">Ping latency</span><span className="font-mono text-slate-700">{data.mongo.latency_ms != null ? `${data.mongo.latency_ms}ms` : "—"}</span></div>
            {data.mongo.error && <p className="text-rose-600 mt-2 leading-snug">{data.mongo.error}</p>}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <p className="label-overline">Missing required env</p>
          {data.missing_required.length === 0 ? (
            <p className="text-sm text-emerald-700 mt-2" data-testid="health-missing-none">All required vars present.</p>
          ) : (
            <ul className="mt-2 text-xs space-y-1.5" data-testid="health-missing-list">
              {data.missing_required.map((n) => (
                <li key={n} className="font-mono text-rose-700">{n}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-md p-5 mt-5">
        <p className="label-overline mb-3">Environment variables</p>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <th className="text-left py-2 font-medium">Name</th>
              <th className="text-left py-2 font-medium">Value preview</th>
              <th className="text-right py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody data-testid="health-env-table">
            {data.env.map((row) => (
              <tr key={row.name} className="border-b border-slate-50 last:border-b-0">
                <td className="py-2 font-mono text-slate-700">{row.name}{row.required && <span className="text-rose-500 ml-1">*</span>}</td>
                <td className="py-2 font-mono text-xs text-slate-500">{row.value ?? <span className="italic text-slate-400">—</span>}</td>
                <td className="py-2 text-right"><HealthBadge status={row.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-3">Checked at {new Date(data.checked_at).toLocaleString()}.</p>

      <div className="bg-white border border-slate-200 rounded-md p-5 mt-5" data-testid="health-probe-card">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div className="flex-1">
            <p className="label-overline">Resend live probe</p>
            <p className="text-xs text-slate-500 mt-1">
              Fires a real verification email — same code path users hit. Leave blank to send to your admin address, or paste a stuck recipient to reproduce a 403.
            </p>
            <Input
              type="email"
              value={probeRecipient}
              onChange={(e) => setProbeRecipient(e.target.value)}
              placeholder="optional: recipient@example.com"
              className="mt-3 max-w-sm font-mono text-sm"
              data-testid="health-probe-recipient"
            />
          </div>
          <Button onClick={runProbe} disabled={probeBusy} size="sm" data-testid="health-probe-button" className="bg-slate-900 hover:bg-slate-800 text-white">
            {probeBusy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending…</> : "Send test email"}
          </Button>
        </div>
        {probeResult && (
          <div
            className={`mt-4 p-3 rounded-sm text-xs border ${probeResult.sent ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}
            data-testid="health-probe-result"
          >
            <div className="flex items-center gap-2 font-mono">
              <span className={probeResult.sent ? "text-emerald-700" : "text-rose-700"}>
                {probeResult.sent ? "DELIVERED" : "FAILED"}
              </span>
              {probeResult.error_code != null && <span className="text-slate-500">· code {probeResult.error_code}</span>}
              {probeResult.error_kind && <span className="text-slate-500">· kind {probeResult.error_kind}</span>}
            </div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2 text-slate-700">
              <div><span className="text-slate-500">To:</span> <span className="font-mono">{probeResult.to_email}</span></div>
              <div><span className="text-slate-500">From:</span> <span className="font-mono">{probeResult.sender_email}</span></div>
              {probeResult.email_id && <div className="col-span-full"><span className="text-slate-500">Email ID:</span> <span className="font-mono">{probeResult.email_id}</span></div>}
            </div>
            {probeResult.error && (
              <p className="mt-2 text-rose-700 leading-snug whitespace-pre-wrap break-words" data-testid="health-probe-error">{probeResult.error}</p>
            )}
            {!probeResult.sent && probeResult.error_kind && KIND_HINTS[probeResult.error_kind] && (
              <div className="mt-3 p-2.5 bg-white border border-rose-200 rounded-sm">
                <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">How to fix</p>
                <p className="text-slate-700 leading-snug">{KIND_HINTS[probeResult.error_kind]}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <UserUnlockCard />
      <BackTagCard />
      <EmailForecastCard />
    </section>
  );
}

// Phase 2c — Admin bulk back-tag dialog for historical data rows.
function BackTagCard() {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("workspace"); // workspace | program
  const [programId, setProgramId] = useState("");
  const [funding, setFunding] = useState("");
  const [partner, setPartner] = useState("");
  const [beneficiary, setBeneficiary] = useState("");
  const [onlyUnassigned, setOnlyUnassigned] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const body = () => ({
    program_id: scope === "program" ? programId : null,
    funding_source: funding || null,
    implementing_partner: partner || null,
    beneficiary_group: beneficiary || null,
    only_unassigned: onlyUnassigned,
  });
  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backtag-rows", { ...body(), dry_run: true });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };
  const apply = async () => {
    if (!preview) return;
    if (!window.confirm(`Apply tags to ${preview.matched_count} rows? This cannot be auto-undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backtag-rows", { ...body(), dry_run: false });
      toast.success(`Applied to ${data.modified_count} rows`);
      setOpen(false);
      setPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-md border border-slate-200 p-5 mt-5" data-testid="admin-backtag-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="label-overline flex items-center gap-1.5">
            <FolderKanban className="w-3.5 h-3.5 text-slate-500" /> Back-tag BI dimensions
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Bulk-assign Funding Source · Implementing Partner · Beneficiary Group to historical rows.
            Always dry-runs first so you see what changes before applying.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOpen(true)}
          data-testid="admin-backtag-open"
        >
          Bulk tag…
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setPreview(null); } }}>
        <DialogContent className="max-w-lg" data-testid="admin-backtag-dialog">
          <DialogHeader>
            <DialogTitle>Bulk back-tag historical rows</DialogTitle>
            <DialogDescription>
              Set BI dimensions on rows where they&apos;re currently unassigned.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="label-overline">Scope</Label>
              <div className="mt-1 flex items-center gap-2 text-sm">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" checked={scope === "workspace"} onChange={() => setScope("workspace")} data-testid="admin-backtag-scope-workspace" />
                  All programs
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" checked={scope === "program"} onChange={() => setScope("program")} data-testid="admin-backtag-scope-program" />
                  Single program
                </label>
              </div>
              {scope === "program" && (
                <Input
                  value={programId}
                  onChange={(e) => setProgramId(e.target.value)}
                  placeholder="Program ID (UUID)"
                  className="mt-2"
                  data-testid="admin-backtag-program-id"
                />
              )}
            </div>
            <div>
              <Label className="label-overline">Funding source</Label>
              <Input value={funding} onChange={(e) => setFunding(e.target.value)} placeholder="e.g. World Bank" data-testid="admin-backtag-funding" />
            </div>
            <div>
              <Label className="label-overline">Implementing partner</Label>
              <Input value={partner} onChange={(e) => setPartner(e.target.value)} placeholder="e.g. CARE Nigeria" data-testid="admin-backtag-partner" />
            </div>
            <div>
              <Label className="label-overline">Beneficiary group</Label>
              <Input value={beneficiary} onChange={(e) => setBeneficiary(e.target.value)} placeholder="e.g. Adolescent girls" data-testid="admin-backtag-beneficiary" />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={onlyUnassigned}
                onChange={(e) => setOnlyUnassigned(e.target.checked)}
                data-testid="admin-backtag-only-unassigned"
              />
              Only update rows where the dim is currently unassigned (safe default)
            </label>
            {preview && (
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs" data-testid="admin-backtag-preview">
                <p className="font-medium text-slate-700">Preview ({preview.scope})</p>
                <p className="text-slate-600 mt-1">
                  Will update <span className="font-mono font-semibold">{preview.matched_count}</span> row
                  {preview.matched_count === 1 ? "" : "s"} with:{" "}
                  <span className="font-mono">{JSON.stringify(preview.would_update)}</span>
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 mt-2">
            <Button variant="ghost" onClick={() => { setOpen(false); setPreview(null); }} data-testid="admin-backtag-cancel">Cancel</Button>
            {!preview ? (
              <Button onClick={runPreview} disabled={busy} data-testid="admin-backtag-preview-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Preview
              </Button>
            ) : (
              <Button onClick={apply} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="admin-backtag-apply-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Apply to {preview.matched_count} rows
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// Email volume forecast — 7-day projection (Phase 1.5+)
// ============================================================
// Surfaces /api/admin/email-volume-forecast as a compact card so admins
// can spot Resend-throttle spikes before they happen. The endpoint
// already computes a full 30-day timeline; we slice the next 7 days
// for the headline and link to the JSON for the deeper view.
function EmailForecastCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/admin/email-volume-forecast?days_ahead=7")
      .then(({ data: d }) => setData(d))
      .catch((err) => setError(err.response?.data?.detail || "Couldn't load forecast"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-5 mt-5" data-testid="email-forecast-loading">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white border border-rose-200 rounded-md p-5 mt-5" data-testid="email-forecast-error">
        <p className="label-overline">Email volume forecast</p>
        <p className="text-sm text-rose-700 mt-2">{error}</p>
      </div>
    );
  }
  const total = data.total_forecast_sends || 0;
  const peak = data.peak_day || { date: null, count: 0 };
  const cap = data.free_tier_daily_cap || 100;
  const overCap = data.days_over_free_cap || 0;
  const cohort = data.growth_cohort_size || 0;
  const headlineTone = overCap > 0 ? "rose" : total > cap ? "amber" : "emerald";
  const headlineClasses = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    rose: "border-rose-200 bg-rose-50 text-rose-900",
  }[headlineTone];

  // Tiny inline sparkline — pure SVG, no chart lib.
  const tl = data.timeline || [];
  const max = Math.max(1, ...tl.map((r) => r.count));
  const sparkW = 160;
  const sparkH = 36;
  const points = tl
    .map((r, i) => {
      const x = (i / Math.max(1, tl.length - 1)) * sparkW;
      const y = sparkH - (r.count / max) * sparkH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const prettyPeak = peak.date
    ? `${peak.count} on ${new Date(peak.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
    : "—";

  return (
    <div
      className="bg-white border border-slate-200 rounded-md p-5 mt-5"
      data-testid="email-forecast-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-slate-500" />
            <p className="label-overline">Email volume forecast · next 7 days</p>
          </div>
          <div className="mt-3 flex items-baseline gap-3">
            <span
              className={`font-heading text-3xl font-semibold ${overCap > 0 ? "text-rose-700" : "text-slate-900"}`}
              data-testid="email-forecast-total"
            >
              {total}
            </span>
            <span className="text-xs text-slate-500">projected sends</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4 text-xs">
            <div>
              <p className="text-slate-500">Peak day</p>
              <p className="font-mono text-slate-900 mt-0.5" data-testid="email-forecast-peak">{prettyPeak}</p>
            </div>
            <div>
              <p className="text-slate-500">Growth cohort</p>
              <p className="font-mono text-slate-900 mt-0.5">{cohort}</p>
            </div>
            <div>
              <p className="text-slate-500">Free-tier cap</p>
              <p className="font-mono text-slate-900 mt-0.5">{cap}/day</p>
            </div>
          </div>
          <div
            className={`mt-3 text-xs px-2 py-1.5 rounded-sm border inline-flex items-center gap-1.5 ${headlineClasses}`}
            data-testid="email-forecast-status"
          >
            {overCap > 0 ? (
              <>
                <AlertTriangle className="w-3 h-3" />
                {overCap} day{overCap === 1 ? "" : "s"} over cap — Resend will throttle
              </>
            ) : total === 0 ? (
              "No expiry emails scheduled in the next 7 days"
            ) : (
              <>
                <ShieldCheck className="w-3 h-3" />
                Within free-tier limits
              </>
            )}
          </div>
        </div>
        <svg
          width={sparkW}
          height={sparkH}
          viewBox={`0 0 ${sparkW} ${sparkH}`}
          className="shrink-0 mt-1"
          aria-label="7-day email volume sparkline"
          data-testid="email-forecast-sparkline"
        >
          <polyline
            fill="none"
            stroke={overCap > 0 ? "#e11d48" : "#10b981"}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            points={points}
          />
        </svg>
      </div>
    </div>
  );
}

// ============================================================
// Manual user unlock — support escalation tool
// ============================================================
// Why a dedicated card: every locked-out user ticket today requires
// SSH into Mongo Atlas + two manual updates. This collapses it to a
// single click, with the same idempotent endpoint used by support.
function UserUnlockCard() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!email.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(`/admin/users/${encodeURIComponent(email.trim().toLowerCase())}/unlock`);
      setResult({ ok: true, ...data });
    } catch (err) {
      setResult({ ok: false, error: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 mt-5" data-testid="user-unlock-card">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="flex-1">
          <p className="label-overline">Unlock locked-out user</p>
          <p className="text-xs text-slate-500 mt-1">
            One-click reset: clears recent login_attempts AND sets email_verified=true. Use this when a user is stuck behind brute-force protection or never received the verification email.
          </p>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            className="mt-3 max-w-sm font-mono text-sm"
            data-testid="unlock-email-input"
          />
        </div>
        <Button
          onClick={submit}
          disabled={busy || !email.trim()}
          size="sm"
          className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium"
          data-testid="unlock-submit-btn"
        >
          {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Unlocking…</> : "Unlock user"}
        </Button>
      </div>
      {result && (
        <div
          className={`mt-4 p-3 rounded-sm text-xs border ${result.ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}
          data-testid="unlock-result"
        >
          {result.ok ? (
            <>
              <p className="font-mono text-emerald-800">✓ {result.message}</p>
              <p className="mt-1 text-emerald-700">
                Previously verified: <span className="font-mono">{String(result.previously_verified)}</span> ·
                login_attempts cleared: <span className="font-mono">{result.login_attempts_cleared}</span>
              </p>
            </>
          ) : (
            <p className="font-mono text-rose-700">✗ {result.error}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Phase 3.25 — Platform Command Center (uche-only). Lazy-loaded after
  // the main analytics paint so non-owner admins never trigger the
  // restricted endpoints.
  const isPlatformOwner = (user?.email || "").toLowerCase() === "uche@kuvora.com";
  const [pccUsers, setPccUsers] = useState(null);
  const [pccFeed, setPccFeed] = useState(null);
  const [pccGrowth, setPccGrowth] = useState(null);
  const [pccLoading, setPccLoading] = useState(false);

  useEffect(() => {
    if (user && user.role !== "admin") {
      setError("Admin role required to view this page.");
      setLoading(false);
      return;
    }
    api.get("/admin/analytics").then(({ data: d }) => setData(d))
      .catch((err) => setError(err.response?.data?.detail || "Couldn't load analytics"))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    if (!isPlatformOwner) return;
    setPccLoading(true);
    Promise.all([
      api.get("/admin/platform/users"),
      api.get("/admin/platform/activity-feed?limit=50"),
      api.get("/admin/platform/growth-metrics"),
    ]).then(([u, f, g]) => {
      setPccUsers(u.data);
      setPccFeed(f.data);
      setPccGrowth(g.data);
    }).catch(() => {
      // Silent fail — non-owner admins or transient errors shouldn't block
      // the main analytics view.
    }).finally(() => setPccLoading(false));
  }, [isPlatformOwner]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white border border-rose-200 rounded-md p-8 text-center" data-testid="admin-analytics-error">
          <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto" />
          <p className="text-slate-900 font-medium mt-3">{error}</p>
          <Link to="/" className="text-sm text-emerald-700 underline mt-4 inline-block">Back to dashboard</Link>
        </div>
      </div>
    );
  }
  const { summary, usage, data_quality, form_performance, report_quality } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/array-logo.png" alt="Array Engine" className="h-7 w-auto" />
          </Link>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="admin-analytics-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-analytics-page">
        <div className="mb-8">
          <p className="label-overline flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-emerald-600" /> Array Engine Intelligence
          </p>
          <h1 className="font-heading text-3xl tracking-tight font-semibold text-slate-900 mt-2">Platform analytics</h1>
          <p className="text-slate-500 mt-1">Is the product working, and where should you invest next?</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          <MetricTile testid="kpi-users" label="Users" value={summary.total_users} sublabel={`${summary.verified_users} verified`} icon={Users} tone="slate" />
          <MetricTile testid="kpi-programs" label="Programs" value={summary.total_programs} icon={FolderKanban} tone="emerald" />
          <MetricTile testid="kpi-reports" label="Reports" value={summary.total_reports} icon={FileText} tone="blue" />
          <MetricTile testid="kpi-tts" label="Median time-to-report" value={`${usage.time_to_report.median_hours}h`} sublabel={`${usage.time_to_report.samples} samples`} icon={ClipboardCheck} tone="amber" />
        </div>

        {isPlatformOwner && (
          <PlatformCommandCenter
            growth={pccGrowth}
            users={pccUsers}
            feed={pccFeed}
            loading={pccLoading}
          />
        )}

        <HealthPanel />

        {/* USAGE */}
        <section className="mb-12">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Usage & adoption</h2>
          <div className="grid lg:grid-cols-3 gap-5 mt-4">
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <p className="label-overline">Programs created · last 30 days</p>
              <p className="text-2xl font-semibold text-slate-900 mt-2">{usage.programs_timeline.reduce((s, p) => s + p.count, 0)}</p>
              <p className="text-xs text-slate-500">across the window</p>
              <div className="mt-3">
                <Sparkline points={usage.programs_timeline} />
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <p className="label-overline">Data path split</p>
              <p className="text-xs text-slate-500 mt-1">How users actually feed data into the system.</p>
              <div className="mt-3">
                <PathSplitDonut split={usage.path_split} />
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <p className="label-overline flex items-center gap-1.5"><TrendingDown className="w-3.5 h-3.5" /> Drop-off funnel</p>
              <p className="text-xs text-slate-500 mt-1">From program creation to a generated report.</p>
              <div className="mt-4">
                <FunnelBars funnel={usage.funnel} />
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 text-xs space-y-1 text-slate-500">
                <div className="flex justify-between"><span>No indicators:</span><span className="font-mono text-rose-600">{usage.funnel.drop_off_pct.no_indicators}%</span></div>
                <div className="flex justify-between"><span>No data:</span><span className="font-mono text-rose-600">{usage.funnel.drop_off_pct.no_data}%</span></div>
                <div className="flex justify-between"><span>No report:</span><span className="font-mono text-rose-600">{usage.funnel.drop_off_pct.no_report}%</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* DATA QUALITY */}
        <section className="mb-12">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Data quality patterns</h2>
          <div className="grid lg:grid-cols-2 gap-5 mt-4">
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <p className="label-overline">Quality across all rows</p>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div>
                  <p className="text-xs text-slate-500">Total rows</p>
                  <p className="text-2xl font-semibold text-slate-900">{data_quality.total_rows}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Dirty</p>
                  <p className="text-2xl font-semibold text-rose-600">{data_quality.dirty_rows}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Clean</p>
                  <p className="text-2xl font-semibold text-emerald-600">{data_quality.clean_rows}</p>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 text-xs space-y-1 text-slate-500">
                <div className="flex justify-between"><span>Avg dirty rate per upload</span><span className="font-mono">{data_quality.avg_dirty_pct_per_upload}%</span></div>
                <div className="flex justify-between"><span>Reports published despite dirty rows</span><span className="font-mono text-amber-700">{data_quality.pct_reports_published_despite_dirty}%</span></div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <p className="label-overline">Most common validation errors</p>
              <p className="text-xs text-slate-500 mt-1 mb-3">Decide whether to invest more in format checking or duplicate detection.</p>
              <ErrorTable
                rows={data_quality.top_errors}
                columns={[{ key: "code", label: "Error code" }, { key: "count", label: "Count" }]}
                testid="data-quality-top-errors"
              />
            </div>
          </div>
        </section>

        {/* FORM PERFORMANCE */}
        <section className="mb-12">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Form collection performance</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
            <MetricTile testid="form-published" label="Published forms" value={form_performance.forms_published} sublabel={`${form_performance.forms_total} total`} icon={ClipboardCheck} tone="slate" />
            <MetricTile testid="form-submissions" label="Submissions" value={form_performance.submissions_total} icon={BarChart3} tone="emerald" />
            <MetricTile testid="form-avg-subs" label="Avg subs / form" value={form_performance.avg_submissions_per_form} sublabel="Are respondents completing?" tone="blue" />
            <MetricTile testid="form-tts" label="Median time-to-first-submission" value={`${form_performance.median_time_to_first_submission_hours}h`} icon={ClipboardCheck} tone="amber" />
          </div>
        </section>

        {/* REPORT QUALITY */}
        <section className="mb-12">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Report quality signals</h2>
          <div className="grid lg:grid-cols-3 gap-5 mt-4">
            <MetricTile testid="report-regenerations" label="Regenerations" value={report_quality.regenerations} sublabel={`${report_quality.avg_regenerations_per_report} avg / report — high = first draft isn't trusted`} icon={TrendingDown} tone="amber" />
            <MetricTile testid="report-exports" label="Export rate" value={`${report_quality.export_rate_pct}%`} sublabel={`${report_quality.exports} exports across ${report_quality.reports} reports`} icon={FileText} tone="emerald" />
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <p className="label-overline flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Most-audited sections</p>
              <p className="text-xs text-slate-500 mt-1 mb-3">High traceability clicks = high value or low trust.</p>
              <ErrorTable
                rows={report_quality.top_traceability_sections}
                columns={[{ key: "section", label: "Section" }, { key: "clicks", label: "Clicks" }]}
                testid="report-top-sections"
              />
            </div>
          </div>
        </section>

        <p className="text-xs text-slate-400 mt-6">
          Generated at {new Date(data.generated_at).toLocaleString()} · numbers compute live, no cache.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 3.25 — Platform Command Center
// Locked to uche@kuvora.com only. Shows directory + audit feed + growth.
// ---------------------------------------------------------------------------
const EVENT_ICONS = {
  program_created: FolderKanban,
  form_created: FilePlus,
  report_generated: FileText,
  upload_created: Upload,
};
const EVENT_TONES = {
  program_created: "bg-emerald-50 text-emerald-700 border-emerald-200",
  form_created: "bg-blue-50 text-blue-700 border-blue-200",
  report_generated: "bg-amber-50 text-amber-800 border-amber-200",
  upload_created: "bg-violet-50 text-violet-700 border-violet-200",
};

function PlatformCommandCenter({ growth, users, feed, loading }) {
  return (
    <section
      className="mb-12 -mx-4 sm:-mx-0"
      data-testid="platform-command-center"
    >
      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        <div className="bg-slate-900 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <p className="font-heading text-sm font-semibold tracking-tight">
              Platform Command Center
            </p>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-medium">
              Owner-only
            </span>
          </div>
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        </div>

        {/* Growth metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 border-b border-slate-100">
          <GrowthTile
            label="Total Users"
            value={growth?.total_users ?? "—"}
            icon={Users}
            testid="pcc-total-users"
          />
          <GrowthTile
            label="New Sign-ups · 7 days"
            value={growth?.new_signups_7d ?? "—"}
            icon={UserPlus}
            divider
            testid="pcc-new-signups"
          />
          <GrowthTile
            label="Active Organizations"
            value={growth?.active_organizations ?? "—"}
            icon={Building2}
            divider
            testid="pcc-active-orgs"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
          {/* Activity Feed */}
          <div className="lg:col-span-2 border-r border-slate-100">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
              <Activity className="w-3.5 h-3.5 text-emerald-600" />
              <p className="label-overline">Real-time activity feed</p>
              <span className="ml-auto text-[10px] text-slate-400">{feed?.total ?? 0} events</span>
            </div>
            <ol className="max-h-[420px] overflow-y-auto divide-y divide-slate-100" data-testid="pcc-feed">
              {(feed?.events || []).length === 0 && (
                <li className="px-5 py-8 text-center text-xs text-slate-400">
                  No platform activity yet. Create a program or upload data to populate this feed.
                </li>
              )}
              {(feed?.events || []).map((e) => {
                const Icon = EVENT_ICONS[e.type] || Activity;
                const tone = EVENT_TONES[e.type] || "bg-slate-50 text-slate-600 border-slate-200";
                return (
                  <li
                    key={e.id}
                    className="px-5 py-3 flex items-start gap-3"
                    data-testid={`pcc-feed-event-${e.id}`}
                  >
                    <span className={`mt-0.5 w-7 h-7 grid place-items-center rounded-sm border shrink-0 ${tone}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-900 leading-snug">{e.sentence}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {e.actor_org ? `${e.actor_org} · ` : ""}
                        {e.created_at ? new Date(e.created_at).toLocaleString() : ""}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* User Directory */}
          <div className="lg:col-span-3">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-emerald-600" />
              <p className="label-overline">User directory</p>
              <span className="ml-auto text-[10px] text-slate-400">{users?.total ?? 0} users</span>
            </div>
            <div className="max-h-[420px] overflow-y-auto" data-testid="pcc-users">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-2 font-medium">Name</th>
                    <th className="px-5 py-2 font-medium">Email</th>
                    <th className="px-5 py-2 font-medium">Organization</th>
                    <th className="px-5 py-2 font-medium">Industry</th>
                    <th className="px-5 py-2 font-medium text-right">Join Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(users?.users || []).length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-5 py-8 text-center text-slate-400">No users yet.</td>
                    </tr>
                  )}
                  {(users?.users || []).map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50" data-testid={`pcc-user-${u.id}`}>
                      <td className="px-5 py-2 font-medium text-slate-900">
                        {u.name || "—"}
                        {u.role === "admin" && (
                          <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1 py-0.5 bg-slate-900 text-white rounded-sm">admin</span>
                        )}
                      </td>
                      <td className="px-5 py-2 font-mono text-[11px] text-slate-700 truncate max-w-[200px]">{u.email}</td>
                      <td className="px-5 py-2 text-slate-700">{u.org_name || <span className="text-slate-300">—</span>}</td>
                      <td className="px-5 py-2 text-slate-700">{u.industry || <span className="text-slate-300">—</span>}</td>
                      <td className="px-5 py-2 text-right text-slate-500 whitespace-nowrap">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function GrowthTile({ label, value, icon: Icon, divider, testid }) {
  return (
    <div
      className={`px-5 py-5 ${divider ? "border-t sm:border-t-0 sm:border-l border-slate-100" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <p className="label-overline">{label}</p>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <p className="font-heading text-3xl font-semibold text-slate-900 mt-2">
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
    </div>
  );
}

